<template>
  <div class="push-management">
    <div class="page-header">
      <div>
        <h2>消息发送记录</h2>
        <p>查看各业务的推送内容，以及 App、微信、短信的发送结果</p>
      </div>
      <a-button icon="setting" @click="$router.push('/sys/push/sms-receivers')">短信接收配置</a-button>
    </div>

    <div class="filter-panel">
      <a-form layout="inline" @submit.prevent="search">
        <a-row :gutter="24">
          <a-col v-if="allTenantsAllowed" :xl="8" :md="12" :xs="24">
            <a-form-item label="所属租户">
              <a-select v-model="query.tenantId" placeholder="全部租户" allowClear showSearch optionFilterProp="children" :loading="tenantsLoading" @focus="loadTenants">
                <a-select-option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">{{ tenantText(tenant.id,tenant.name) }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xl="8" :md="12" :xs="24">
            <a-form-item label="业务类型">
              <a-select v-model="query.businessType" placeholder="全部业务" allowClear>
                <a-select-option v-for="(name,key) in businesses" :key="key" :value="key">{{ name }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xl="8" :md="12" :xs="24">
            <a-form-item label="推送渠道">
              <a-select v-model="query.channel" placeholder="全部渠道" allowClear>
                <a-select-option v-for="(name,key) in channels" :key="key" :value="key">{{ name }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xl="8" :md="12" :xs="24">
            <a-form-item label="发送状态">
              <a-select v-model="query.state" placeholder="全部状态" allowClear>
                <a-select-option v-for="(name,key) in states" :key="key" :value="key">{{ name }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xl="8" :md="12" :xs="24">
            <a-form-item label="接收人">
              <a-input v-model="query.receiver" placeholder="请输入姓名" allowClear @pressEnter="search"/>
            </a-form-item>
          </a-col>
          <a-col :xl="allTenantsAllowed ? 8 : 10" :md="12" :xs="24">
            <a-form-item label="发送时间">
              <a-range-picker v-model="dateRange" style="width:100%;min-width:0" showTime format="YYYY-MM-DD HH:mm:ss" :placeholder="['开始时间','结束时间']" @change="dates"/>
            </a-form-item>
          </a-col>
          <a-col :xl="allTenantsAllowed ? 24 : 6" :md="allTenantsAllowed ? 24 : 12" :xs="24" class="filter-actions">
            <a-button type="primary" icon="search" :loading="loading" htmlType="submit">查询</a-button>
            <a-button icon="reload" :disabled="loading" @click="reset">重置</a-button>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <a-alert v-if="error" class="page-alert" type="error" :message="error" showIcon/>
    <a-alert v-else-if="!enabled" class="page-alert" type="info" message="统一推送当前未开启" description="启用统一推送后，此处将展示新生成的发送记录。" showIcon/>
    <a-alert v-if="allTenantsAllowed && tenantError" class="page-alert" type="warning" :message="tenantError" description="点击租户筛选可重新加载，当前发送记录仍可查看。" showIcon/>
    <div class="table-toolbar">
      <span>发送记录 <span class="record-count">共 {{ pagination.total }} 条</span></span>
      <a-button icon="sync" :loading="loading" @click="load(pagination.current)">刷新</a-button>
    </div>
    <a-table
      size="middle"
      rowKey="id"
      :columns="columns"
      :dataSource="records"
      :loading="loading"
      :pagination="false"
      :scroll="{x:allTenantsAllowed ? 1440 : 1260}"
      :locale="{emptyText: error ? '加载失败，请重试' : '暂无符合条件的发送记录'}"
    >
      <span slot="business" slot-scope="value" class="business-label">{{ businesses[value] || value || '其他通知' }}</span>
      <span slot="tenant" slot-scope="value,record">{{ tenantText(record.tenantId,value) }}</span>
      <div slot="message" slot-scope="value,record" class="message-cell">
        <a class="message-title" :title="value" @click="$refs.detail.open(record.id,false)">{{ value || '未命名消息' }}</a>
        <div class="message-summary" :title="record.summary || record.content">{{ record.summary || record.content || '暂无内容摘要' }}</div>
      </div>
      <span slot="receiver" slot-scope="value">{{ value || '未记录' }}</span>
      <div slot="channels" slot-scope="value" class="channel-tags">
        <a-tag v-for="(state,channel) in value" :key="channel" :color="stateColors[state]">{{ channels[channel] || channel }} · {{ states[state] || state }}</a-tag>
        <span v-if="!value || !Object.keys(value).length" class="muted">暂无渠道记录</span>
      </div>
      <span slot="total" slot-scope="value">{{ value || 0 }} 条</span>
      <a slot="action" slot-scope="value,record" @click="$refs.detail.open(record.id,false)">查看详情</a>
    </a-table>
    <div class="push-pagination">
      <span class="pagination-total">共 {{ pagination.total }} 条记录</span>
      <a-pagination
        :current="pagination.current"
        :pageSize="pagination.pageSize"
        :total="pagination.total"
        :pageSizeOptions="pagination.pageSizeOptions"
        :locale="pagination.locale"
        :disabled="loading"
        showSizeChanger
        showQuickJumper
        @change="pageChange"
        @showSizeChange="sizeChange"
      />
    </div>
    <push-message-detail ref="detail"/>
  </div>
</template>

<script>
import { getAction } from '@/api/manage'
import PushMessageDetail from './modules/PushMessageDetail'

/** 统一发送记录页，按菜单授权访问并展示各渠道独立结果。 */
export default {
  name: 'PushManagementList',
  components: { PushMessageDetail },
  /** 筛选条件与分页分别保存，重置时同步清空日期组件。 */
  data() {
    return {
      query: {}, dateRange: [], records: [], loading: false, error: '', enabled: true,
      // 跨租户能力由后端身份校验结果控制，不读取本地租户配置决定权限。
      allTenantsAllowed: false, tenants: [], tenantsLoading: false, tenantsLoaded: false, tenantError: '',
      pagination: {
        current: 1, pageSize: 20, total: 0,
        pageSizeOptions: ['10','20','50'],
        locale: { items_per_page: '条/页', jump_to: '跳至', jump_to_confirm: '确定', page: '页', prev_page: '上一页', next_page: '下一页' }
      },
      businesses: { WORK_PLAN: '工作计划', WARN: '预警通知', APPROVAL: '流程审批', SAFETY: '安全生产', JOB_ORDER: '派工通知', REPORT: '报表通知', ASSET: '资产与运维', OTHER: '其他通知' },
      channels: { APP: 'App', WECHAT: '微信', SMS: '短信' },
      states: { PENDING: '待发送', SENDING: '提交中', SUBMITTED: '已提交', FAILED: '发送失败', SKIPPED: '已跳过', CANCELLED: '已取消', EXPIRED: '已过期', UNKNOWN: '结果未知' },
      stateColors: { PENDING: 'orange', SENDING: 'blue', SUBMITTED: 'green', FAILED: 'red', UNKNOWN: 'purple' },
      baseColumns: [
        { title: '业务类型', dataIndex: 'businessType', width: 130, scopedSlots: { customRender: 'business' } },
        { title: '消息内容', dataIndex: 'title', width: 360, scopedSlots: { customRender: 'message' } },
        { title: '接收人', dataIndex: 'recipientName', width: 120, scopedSlots: { customRender: 'receiver' } },
        { title: '发送时间', dataIndex: 'createTime', width: 190 },
        { title: '渠道 / 状态', dataIndex: 'channelStates', width: 250, scopedSlots: { customRender: 'channels' } },
        { title: '关联事项', dataIndex: 'total', width: 100, align: 'center', scopedSlots: { customRender: 'total' } },
        { title: '操作', key: 'action', width: 110, align: 'center', fixed: 'right', scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  computed: {
    /** 主租户显示所属租户列，普通租户保留原表格布局。 */
    columns() {
      return this.allTenantsAllowed ? [{ title: '所属租户', dataIndex: 'tenantName', width: 180, scopedSlots: { customRender: 'tenant' } }].concat(this.baseColumns) : this.baseColumns
    }
  },
  /** 进入页面读取服务端授权范围内的发送记录。 */
  created() { this.load(1) },
  methods: {
    /** 租户名称不存在时保留编号，避免将旧记录误认作其他租户。 */
    tenantText(id,name) { return (name || (Number(id) === 0 ? '主租户' : '租户')) + '（' + id + '）' },
    /** 独立读取租户选项，加载失败不阻断发送记录查询。 */
    async loadTenants() {
      if (!this.allTenantsAllowed || this.tenantsLoading || this.tenantsLoaded) return
      this.tenantsLoading = true
      this.tenantError = ''
      try {
        const res = await getAction('/sys/tenant/queryAllTenant')
        if (!res.success) throw new Error(res.message || '租户选项加载失败')
        this.tenants = res.result.map(tenant => ({ id: Number(tenant.id), name: tenant.name }))
        if (!this.tenants.some(tenant => tenant.id === 0)) this.tenants.unshift({ id: 0, name: '主租户' })
        this.tenantsLoaded = true
      } catch (e) {
        this.tenantError = e.message || '租户选项加载失败'
      } finally {
        this.tenantsLoading = false
      }
    },
    /** 清空日期时不向接口传递空时间。 */
    dates(value,text) {
      this.query.start = text[0] || undefined
      this.query.end = text[1] || undefined
    },
    /** 每次应用筛选从第一页开始。 */
    search() { this.load(1) },
    /** 重置所有筛选，保留用户选择的每页数量。 */
    reset() {
      this.query = {}
      this.dateRange = []
      this.load(1)
    },
    /** 切换每页数量后回到第一页，避免页码超出范围。 */
    sizeChange(current,size) {
      this.pagination.pageSize = size
      this.load(1)
    },
    /** 独立分页栏切换页码，避免表格内部布局影响分页控件。 */
    pageChange(current) { this.load(current) },
    /** 防止重复请求，失败时清空旧结果，避免误认为筛选已生效。 */
    async load(current) {
      if (this.loading) return
      this.loading = true
      this.error = ''
      try {
        const r = await getAction('/sys/push/messages', { ...this.query, current, size: this.pagination.pageSize })
        if (!r.success) throw new Error(r.message || '发送记录加载失败')
        this.records = r.result.records
        this.pagination.current = r.result.current
        this.pagination.total = r.result.total
        this.enabled = r.result.enabled !== false
        this.allTenantsAllowed = r.result.allTenantsAllowed === true
        if (this.allTenantsAllowed && this.enabled) this.loadTenants()
      } catch (e) {
        this.records = []
        this.pagination.total = 0
        this.error = e.message || '发送记录加载失败，请重试'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.push-management {
  padding: 24px;
  background: #fff;
  border-radius: 6px;
  color: #303133;
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    h2 { margin: 0 0 6px; color: #1f2937; font-size: 20px; font-weight: 600; }
    p { margin: 0; color: #7b8493; }
  }
  .filter-panel { padding: 20px 20px 4px; border: 1px solid #edf0f4; border-radius: 4px; background: #f7f9fc; }
  /deep/ .ant-form-item { display: flex; margin: 0 0 16px; }
  // 此页面采用浅色面板，单独覆盖全局主题的浅色标签，避免白底文字不可见。
  /deep/ .ant-form-item-label { width: 80px; flex-shrink: 0; text-align: left; }
  /deep/ .ant-form-item-label > label { color: #4b5563 !important; }
  /deep/ .ant-form-item-control-wrapper { flex: 1; min-width: 0; }
  .filter-panel /deep/ .ant-select, .filter-panel /deep/ .ant-calendar-picker { width: 100%; }
  .filter-panel /deep/ .ant-form-item-control > span { color: #303133 !important; }
  .filter-actions { display: flex; justify-content: flex-end; margin-bottom: 16px; }
  .filter-actions .ant-btn + .ant-btn { margin-left: 8px; }
  .page-alert { margin-top: 16px; }
  .table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 14px; font-size: 15px; font-weight: 600; }
  .record-count { margin-left: 12px; font-size: 13px; color: #909399; font-weight: normal; }
  .business-label { color: #596579; }
  .message-cell { min-width: 0; }
  .message-title { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
  .message-summary { margin-top: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; color: #909399; }
  .channel-tags { display: flex; flex-wrap: wrap; }
  .channel-tags /deep/ .ant-tag { margin: 3px 6px 3px 0; }
  .muted { color: #909399; }
  /deep/ .ant-table { color: #303133; }
  /deep/ .ant-table-thead > tr > th { background: #f7f9fc; color: #596579; }
  /deep/ .ant-table-tbody > tr > td { border-bottom-color: #edf0f4; }
  /deep/ .ant-table-tbody > tr:hover > td { background: #f5faff; }
  /deep/ .ant-table table { table-layout: fixed; }
  // 分页独立布局并覆盖全局深色主题，防止白底文字消失及下拉框挤占跳页控件。
  .push-pagination {
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;
    padding-top: 18px; gap: 12px; border-top: 1px solid #edf0f4;
    .pagination-total { color: #606266; white-space: nowrap; }
    /deep/ .ant-pagination { display: flex; align-items: center; flex-wrap: wrap; color: #606266 !important; margin: 0; }
    /deep/ .ant-pagination-item a, /deep/ .ant-pagination-item-link { color: #606266 !important; }
    /deep/ .ant-pagination-item, /deep/ .ant-pagination-item-link { background: #fff !important; }
    /deep/ .ant-pagination-item-active { border-color: #1890ff; background: #e6f7ff !important; }
    /deep/ .ant-pagination-item-active a { color: #1890ff !important; }
    /deep/ .ant-pagination-disabled .ant-pagination-item-link { color: #c0c4cc !important; }
    /deep/ .ant-pagination-options { display: inline-flex; align-items: center; margin-left: 8px; }
    /deep/ .ant-pagination-options-size-changer { width: 110px; margin-right: 12px; }
    /deep/ .ant-pagination-options-quick-jumper { white-space: nowrap; color: #606266; }
    /deep/ .ant-pagination-options-quick-jumper input { color: #303133; background: #fff; }
  }
}
@media (max-width: 767px) {
  .push-management {
    padding: 16px;
    .page-header { align-items: flex-start; flex-direction: column; }
    .page-header .ant-btn { margin-top: 12px; }
    .filter-panel { padding: 16px 12px 0; }
    .filter-actions { justify-content: flex-start; padding-left: 92px !important; }
    .push-pagination /deep/ .ant-pagination-options { margin: 10px 0 0; }
  }
}
</style>
